// ---------------------------------------------------------------------------
// keywordReplacer.js
// Replaces leftover campaign data from an uploaded template (e.g. an old
// product name like "NerveCell" in image alt text) with the user's Keyword.
//
//   - Image alt text only changes when it names the old product; unrelated
//     images retain their actual descriptions for accessibility.
//   - The old name is replaced everywhere else in the page (visible text,
//     meta tags, JSON-LD, Next.js data).
//   - URLs, file paths, inline JS and CSS are never changed, so images,
//     links and scripts keep working.
// ---------------------------------------------------------------------------

// Words that are never treated as a brand name when auto-detecting.
const STOPWORDS=new Set([
  'the','and','for','with','from','your','our','this','that','image','images',
  'photo','picture','logo','icon','video','thumbnail','banner','review','reviews',
  'official','website','site','product','bottle','bottles','supplement','buy',
  'now','order','price','customer','customers','results','before','after','star',
  'stars','rating','money','back','guarantee','free','shipping','bonus','click',
  'here','home','page','new','best','top','real','how','what','why','does','work',
  'works','ingredients','benefits','formula','natural','health','day','days',
  'example','sample','placeholder','demo',
]);

const ALT_RX=/(\balt\s*=\s*)(["'])([\s\S]*?)\2/gi;
const JSON_ALT_RX=/("alt"\s*:\s*")((?:[^"\\]|\\.)*)(")/g;
// Same field inside escaped JSON strings (Next.js self.__next_f.push payloads).
const ESCAPED_JSON_ALT_RX=/(\\"alt\\"\s*:\s*\\")((?:(?!\\")[^])*?)(\\")/g;

const escapeRx=(s)=> s.replace(/[.*+?^${}()|[\]\\]/g,'\\$&');

/** Strip characters that would break HTML attributes or JSON strings. */
export const sanitiseKeyword=(value)=> (value || '').replace(/[<>"'\\]/g,'').replace(/\s+/g,' ').trim();

/** Parse "NerveCell, Nerve Cell" into a de-duplicated list, longest first. */
export const parseOldNames=(value)=> [...new Set(
  (value || '').split(/[,\n]/).map((name)=> sanitiseKeyword(name)).filter((name)=> name.length >=2)
)].sort((a,b)=> b.length - a.length);

const nameRx=(names)=> names.length
  ? new RegExp(`(?<![A-Za-z0-9])(?:${names.map(escapeRx).join('|')})(?![A-Za-z0-9])`,'gi')
  : null;

/**
 * Guess the old brand name in a template. Looks at image alt text first
 * (where leftover names are most obvious), then the page title.
 * CamelCase words like "NerveCell" score highest.
 */
export const detectTemplateBrand=(html)=> {
  if (!html) return '';
  const scores=new Map();
  const add=(text,weight)=> {
    for (const word of text.match(/[A-Za-z][A-Za-z0-9]{2,}/g) || []) {
      if (STOPWORDS.has(word.toLowerCase())) continue;
      const camel=/^[A-Z][a-z0-9]+[A-Z][A-Za-z0-9]*$/.test(word);
      const capitalised=/^[A-Z]/.test(word);
      if (!camel && !capitalised) continue;
      scores.set(word,(scores.get(word) || 0) + weight * (camel ? 5 : 1));
    }
  };
  for (const match of html.matchAll(ALT_RX)) add(match[3],3);
  const title=html.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
  if (title) {
    add(title[1],1);
    // A brand in the title is also repeated through the page; a one-off
    // title word is not. Add a bonus for how often each title word appears.
    for (const word of new Set(title[1].match(/[A-Za-z][A-Za-z0-9]{2,}/g) || [])) {
      if (!scores.has(word)) continue;
      const uses=html.split(new RegExp(`(?<![A-Za-z0-9])${escapeRx(word)}(?![A-Za-z0-9])`,'i')).length - 1;
      scores.set(word,scores.get(word) + uses * 0.5);
    }
  }
  const ranked=[...scores.entries()].sort((a,b)=> b[1] - a[1]);
  return ranked[0]?.[0] || '';
};

/**
 * Make body text read naturally: a long search phrase like
 * "SEO NEO Reviews Exclusive Deal (Key Insights) Official-Site" is never
 * something a person writes mid-sentence, so every occurrence is replaced
 * with the plain product name ("In this SEO NEO review"). Accidental
 * doubles ("SEO NEO SEO NEO") are collapsed. Titles are not passed here.
 */
export const naturaliseKeyword=(text,keyword,product)=> {
  if (typeof text!=='string' || !text) return text;
  const phrase=(keyword || '').trim();
  const name=(product || '').trim();
  if (!phrase || !name || phrase.toLowerCase()===name.toLowerCase()) return text;
  const phraseRx=new RegExp(escapeRx(phrase).replace(/\s+/g,'\\s+'),'gi');
  const nameEsc=escapeRx(name);
  return text
    .replace(phraseRx,name)
    .replace(new RegExp(`(${nameEsc})(?:\\s+${nameEsc})+`,'gi'),'$1')
    .replace(/\s{2,}/g,' ');
};

/** Count image alt attributes in a template (for the UI hint). */
export const countAltTexts=(html)=> (html?.match(/<img\b[^>]*\balt\s*=/gi) || []).length;

// URLs (plain or JSON-escaped) and file names like nervecell-bottle.png.
const URL_OR_FILE_RX=/((?:https?:)?(?:\/\/|\\\/\\\/)[^\s"'<>)]+|[\w./\\-]+\.(?:png|jpe?g|gif|webp|svg|avif|ico|mp4|webm|js|css|html?))/gi;

// Inline <script>/<style> blocks. JSON data scripts and Next.js payloads
// (self.__next_f.push) hold page text, so those still get replaced.
const CODE_BLOCK_RX=/(<script\b[^>]*>[\s\S]*?<\/script>|<style\b[^>]*>[\s\S]*?<\/style>)/gi;
const isDataScript=(block)=> /^<script\b[^>]*type="application\/(?:ld\+)?json"/i.test(block) ||
  block.includes('self.__next_f.push');

// Apply `replace` to text, leaving URLs and file paths untouched.
const replaceOutsideUrls=(text,replace)=> text
  .split(URL_OR_FILE_RX)
  .map((part,i)=> (i % 2 ? part : replace(part)))
  .join('');

const rewriteAlt=(value,rx,keyword)=> {
  if (!value.trim()) return value;              // keep decorative alt="" empty
  if (rx) {
    rx.lastIndex=0;
    if (rx.test(value)) {
      rx.lastIndex=0;
      return value.replace(rx,keyword);         // "NerveCell bottle" -> "SEO NEO bottle"
    }
  }
  return value;                                 // preserve an unrelated image's real description
};

/**
 * Apply the keyword to a generated page.
 * @returns {{html: string, altCount: number, nameCount: number}}
 */
export const applyKeywordReplacements=(html,keywordInput,oldNamesInput)=> {
  const keyword=sanitiseKeyword(keywordInput);
  if (!keyword) return {html,altCount: 0,nameCount: 0};
  const names=parseOldNames(oldNamesInput).filter((name)=> name.toLowerCase()!==keyword.toLowerCase());
  const rx=nameRx(names);
  let altCount=0;
  let nameCount=0;

  const rewrite=(text)=> {
    // 1) Alt text: HTML attributes, JSON "alt" fields, escaped JSON "alt" fields.
    //    (The keyword is sanitised, so it is safe inside any of these quotes.)
    const altSwap=(whole,open,value,close)=> {
      altCount++;
      return `${open}${rewriteAlt(value,rx,keyword)}${close}`;
    };
    let out=text
      .replace(ALT_RX,(whole,open,quote,value)=> altSwap(whole,open+quote,value,quote))
      .replace(JSON_ALT_RX,altSwap)
      .replace(ESCAPED_JSON_ALT_RX,altSwap);

    // 2) Old name everywhere else, leaving URLs and file paths untouched.
    //    Handles like @NerveCell can't contain spaces, so they get the
    //    compact form (@seoneo).
    const handle=keyword.toLowerCase().replace(/[^a-z0-9_]/g,'');
    if (rx) out=replaceOutsideUrls(out,(part)=> part.replace(rx,(match,offset,str)=> {
      nameCount++;
      return str[offset - 1]==='@' ? handle : keyword;
    }));
    return out;
  };

  // Inline JS/CSS code is left alone; HTML and data scripts are rewritten.
  const out=html
    .split(CODE_BLOCK_RX)
    .map((part,i)=> (i % 2 && !isDataScript(part) ? part : rewrite(part)))
    .join('');

  return {html: out,altCount,nameCount};
};

// ---------------------------------------------------------------------------
// Sales call-to-action removal
// Pushy, repeated CTAs ("Order X Now", "Act Now", "Claim Your Discount")
// read as spam and add nothing for the reader. They are removed from
// generated text and from leftover template text. URLs, inline code and
// the protected strings (the page title) are never touched.
// ---------------------------------------------------------------------------
const ctaRx=(product)=> {
  const p=product ? escapeRx(product).replace(/\s+/g,'\\s+') : null;
  const name=p ? `(?:${p}\\s+|it\\s+|this\\s+)?` : '(?:it\\s+|this\\s+)?';
  return new RegExp(
    '(?<![A-Za-z0-9])(?:' + [
      `try\\s+${name}(?:today|now)`,
      `order\\s+${name}(?:today|now)`,
      `buy\\s+${name}(?:today|now)`,
      `get\\s+(?:it|yours|${p || 'it'})\\s+(?:today|now)`,
      'act\\s+now',
      'shop\\s+now',
      'click\\s+here(?:\\s+to\\s+(?:buy|order))?',
      'hurry(?:\\s+up)?',
      "don't\\s+miss\\s+out",
      'claim\\s+your\\s+(?:limited\\s+|exclusive\\s+|special\\s+)?(?:discount|offer|deal|bonus)(?:\\s+now|\\s+today)?',
      'limited[-\\s]time\\s+(?:offer|discount|deal)',
      p ? `${p}\\s+where\\s+to\\s+buy` : null,
    ].filter(Boolean).join('|') + ')(?![A-Za-z0-9])[.!]?',
    'gi'
  );
};

/** Remove sales CTAs from a plain string (used on generated text). */
export const stripCtaText=(text,product)=> {
  if (typeof text!=='string' || !text) return text;
  return text
    .replace(ctaRx(product),'')
    .replace(/[ \t]{2,}/g,' ')
    .replace(/\s+([.,!?])/g,'$1')
    .replace(/([.!?])\s*[.!?]+/g,'$1')
    .trim();
};

/**
 * Remove sales CTAs from a whole generated page.
 * @param {string} html
 * @param {string} product
 * @param {string[]} protect - strings left exactly as they are (page title)
 * @returns {{html: string, count: number}}
 */
export const removeSalesCtas=(html,product,protect=[])=> {
  const rx=ctaRx(product);
  let count=0;
  const clean=(text)=> text
    .replace(rx,()=> { count++; return ''; })
    .replace(/[ \t]{2,}/g,' ')
    .replace(/ +(<\/)/g,'$1')
    .replace(/ +([.,!?])(?=\s|<|"|$)/g,'$1');
  const guards=protect.filter(Boolean).map((p)=> escapeRx(p));
  const guardRx=guards.length ? new RegExp(`(${guards.join('|')})`,'g') : null;
  const out=html
    .split(CODE_BLOCK_RX)
    .map((part,i)=> {
      if (i % 2 && !isDataScript(part)) return part;
      const segments=guardRx ? part.split(guardRx) : [part];
      return segments
        .map((seg,j)=> (guardRx && j % 2 ? seg : replaceOutsideUrls(seg,clean)))
        .join('');
    })
    .join('');
  return {html: out,count};
};
